import type {
  Availability,
  ListingResult,
  MatchLevel,
  Money,
  SourceStatus,
  Wear,
} from "@pokifolio/contracts";

/** "$24.99", "£12.50", "PKR 6,500". Falls back to "6500 XYZ" for codes the browser does not know. */
export function formatMoney(money: Money): string {
  const value = Number(money.amount);
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: money.currency }).format(value);
  } catch {
    return `${money.amount} ${money.currency}`;
  }
}

export const MATCH_LABELS: Readonly<Record<MatchLevel, string>> = {
  exact: "Exact match",
  likely: "Likely match",
  uncertain: "Uncertain",
};

export const MATCH_TONES: Readonly<Record<MatchLevel, "success" | "brand" | "neutral">> = {
  exact: "success",
  likely: "brand",
  uncertain: "neutral",
};

export const SOURCE_NAMES: Readonly<Record<SourceStatus["source"], string>> = {
  ebay: "eBay",
  web: "Web search",
};

export function sourceSummary(status: SourceStatus): string {
  const name = SOURCE_NAMES[status.source];
  if (status.status === "ok") return `${name}: ${String(status.resultCount)} found`;
  if (status.status === "not_configured") return `${name}: not set up`;
  return `${name}: failed`;
}

export function listingTypeLabel(type: ListingResult["listingType"]): string | null {
  if (type === "auction") return "Auction";
  if (type === "fixed_price") return "Buy it now";
  return null;
}

export interface AvailabilityView {
  readonly label: string;
  readonly tone: "success" | "warning" | "danger" | "neutral";
  /** Extra line under the label, e.g. when an auction ends. */
  readonly note: string | null;
}

/** "25 Oct, 18:30" in the viewer's time zone. */
export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString([], {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Plain words for whether a listing can still be bought. Only eBay's own answer says "Sold out" or "Ended". */
export function describeAvailability(a: Availability): AvailabilityView {
  const left = a.availableQuantity;
  const ends = a.endsAt === null ? null : `Ends ${formatDateTime(a.endsAt)}`;
  switch (a.status) {
    case "available":
      return {
        label: left !== null && left > 1 ? `Available · ${String(left)} left` : "Available",
        tone: "success",
        note: ends,
      };
    case "limited":
      return {
        label: left === 1 ? "Last one" : left !== null ? `Limited · ${String(left)} left` : "Limited stock",
        tone: "warning",
        note: ends,
      };
    case "sold_out":
      return {
        label: "Sold out",
        tone: "danger",
        note: a.soldQuantity === null ? null : `${String(a.soldQuantity)} sold`,
      };
    case "ended":
      return {
        label: "Ended",
        tone: "danger",
        note: a.endsAt === null ? null : `Ended ${formatDateTime(a.endsAt)}`,
      };
    case "may_be_sold_out":
      return { label: "May be sold out", tone: "warning", note: "From search results; check website" };
    case "unknown":
      return { label: "Not known", tone: "neutral", note: "Check website" };
  }
}

export const WEAR_LABELS: Readonly<Record<Wear, string>> = {
  damaged: "Damaged",
  heavily_played: "Heavily played",
  moderately_played: "Moderately played",
  lightly_played: "Lightly played",
};

/** Lightly played is normal for older cards; the rest deserve a warning before buying. */
export const isWearWarning = (wear: Wear): boolean => wear !== "lightly_played";

/** "14:05" in the viewer's time zone. */
export function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
